/* @flow */

import * as React from 'react';

import {Button} from 'shells/button';
import {colors} from 'shells/colors';
import {LoadingIcon} from 'shells/icon';
import {StackLegacy} from 'shells/layout';

import type {EntitySummaryStatus} from 'nutshell-graphql-types';

import {SummaryInfoButton} from '../power-ai/summary-info-button';
import {useGenerateEntitySummary} from '../power-ai/timeline-summary/graphql/hooks/use-generate-entity-summary';
import {useGetEntitySummary} from '../power-ai/timeline-summary/graphql/hooks/use-get-entity-summary';

import {SummaryButtons} from './summary-buttons';

import './transcript-summary.module.css';

type Props = {
    entityId: string,
    onAdd: () => Promise<*>,
    onReplace: ?() => Promise<*>,
    onCancel: () => void,
};

export function TranscriptSummary(props: Props) {
    const {entitySummary, isRefetching, startPolling, stopPolling} = useGetEntitySummary(
        props.entityId,
        'CALL'
    );
    const {generateEntitySummary, isLoading: isGeneratingSummary} = useGenerateEntitySummary();

    const handleGenerateSummary = () => {
        generateEntitySummary(
            {entityId: props.entityId, summaryType: 'CALL'},
            entitySummary ? entitySummary.id : undefined
        );
    };

    const status: ?EntitySummaryStatus = entitySummary ? entitySummary.status : null;

    React.useEffect(() => {
        // Start polling if there is no entitySummary yet or the entitySummary status is in progress
        if (isGeneratingSummary || status === 'LOADING') {
            startPolling(5000); // 5 seconds
        } else if (status !== 'LOADING') {
            stopPolling();
        }

        // Cleanup on unmount
        return () => stopPolling();
    }, [isGeneratingSummary, status, startPolling, stopPolling]);

    const isLoadingState =
        // refetching after initial generation
        (!entitySummary && isRefetching) ||
        // regenerating a new summary
        (isGeneratingSummary && entitySummary);

    const isInProgress =
        // initial generation
        isGeneratingSummary ||
        // summary generation is in progress
        status === 'LOADING';

    let transcriptSummaryContent;
    let showButtons = false;
    if (isLoadingState) {
        transcriptSummaryContent = (
            <div className='flex justify-center'>
                <LoadingIcon size={48} fill={colors.orangeLt} />
            </div>
        );
    } else if (isInProgress) {
        transcriptSummaryContent = <div>AI is generating a new summary of this transcript…</div>;
    } else if (entitySummary) {
        switch (entitySummary.status) {
            case 'ERRORED':
                transcriptSummaryContent = (
                    <StackLegacy spacing={4} isHorizontal={true}>
                        <div>There was a problem generating your AI summary.</div>
                        <Button
                            noPadding={true}
                            variant='text-primary'
                            onClick={handleGenerateSummary}
                        >
                            Try again
                        </Button>
                    </StackLegacy>
                );
                break;
            case 'COMPLETE':
                transcriptSummaryContent = <div>{entitySummary.summary}</div>;
                showButtons = true;
                break;
        }
    }

    if (transcriptSummaryContent) {
        return (
            <div styleName='container'>
                {transcriptSummaryContent}
                {showButtons ? (
                    <div styleName='footer'>
                        <SummaryInfoButton variant='zoom' />
                        <SummaryButtons
                            onAdd={props.onAdd}
                            onReplace={props.onReplace}
                            onCancel={props.onCancel}
                        />
                    </div>
                ) : undefined}
            </div>
        );
    }

    return <></>;
}
